import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtPct } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { PageHeader } from "../components/PageHeader";
import { IconUser } from "../components/icons";
import type { TransportDriverCommissionDto } from "@bi/types";

/** % (0..1 no DTO) → texto; "—" quando null. */
const pct = (v: number | null) => (v == null ? "—" : fmtPct(v * 100));

export function DriversPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading } = useQuery({
    queryKey: ["transport", "drivers", from, to],
    queryFn: () =>
      api.get<TransportDriverCommissionDto>(
        `/dashboards/transport/drivers?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const byDriver = data?.byDriver ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Motoristas"
        subtitle="Comissão por motorista (acertos): base de fretes, percentual e ranking"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPIStatCard
          label="Comissão Total" memory={{ cardKey: "motoristas.comissao", from, to, expected: data?.totalCommission }}
          value={data ? data.totalCommissionFormatted : "—"}
          status={data && data.totalCommission > 0 ? "warning" : "neutral"} loading={isLoading}
        />
        <KPIStatCard
          label="Base de Fretes" memory={{ cardKey: "motoristas.base-fretes", from, to, expected: data?.totalFreights }}
          value={data ? data.totalFreightsFormatted : "—"}
          status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Percentual Médio" memory={{ cardKey: "motoristas.percentual", from, to, expected: data?.avgCommissionPct == null ? null : data.avgCommissionPct * 100 }}
          value={data ? pct(data.avgCommissionPct) : "—"}
          valueSub="comissão / fretes"
          status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Motoristas" memory={{ cardKey: "motoristas.quantidade", from, to, expected: data?.driverCount }}
          value={data ? fmtNumber(data.driverCount) : "—"}
          valueSub={data ? `${fmtNumber(data.settlementCount)} acertos` : undefined}
          status="neutral" loading={isLoading}
        />
      </div>

      {/* Ranking por motorista */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <IconUser className="w-4 h-4 text-gray-400" />
            Comissão por Motorista
          </p>
        </div>
        <div className="divide-y divide-surface-border">
          {isLoading ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
          ) : byDriver.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem acertos no período.</p>
          ) : byDriver.map((d, i) => (
            <div key={`${d.name}-${i}`} className="px-5 py-3 flex items-center gap-3">
              <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{d.name}</p>
                <p className="text-xs text-gray-400">
                  {[
                    `fretes ${d.totalFreightsFormatted}`,
                    `${pct(d.commissionPct)}`,
                    d.settlementCount > 1 ? `${d.settlementCount} acertos` : null,
                  ].filter(Boolean).join(" · ")}
                </p>
              </div>
              <p className="text-sm font-semibold text-gray-900 flex-shrink-0">{d.commissionFormatted}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
